/**
 * L1J Web Client - ChatBox Component
 * 채팅창 UI 컴포넌트
 *
 * ── 채널 규약 ──────────────────────────────────────────────────────────────
 * 게이트웨이가 혈맹/파티/전체/연합/트레이드/귓속말을 채널을 붙여 server_chat으로
 * 보낸다. ChatMessage.type이 그 채널이고, 색과 말머리와 탭 필터가 전부 이 값
 * 하나를 기준으로 갈린다.
 *
 * ── 발신자를 다시 붙이지 않는다 ────────────────────────────────────────────
 * 서버 S_ChatPacket은 본문 문자열 자체를 "이름: 내용" / "<이름> 내용" /
 * "[이름] 내용" 같은 **완성된 형태**로 만들어 보낸다
 * (writeS(pc.getName() + ": " + chat)). 여기서 sender를 한 번 더 앞에 붙이면
 * "이름: 이름: 내용"이 된다. 그래서 렌더링에는 message만 쓰고 sender는 쓰지
 * 않는다 (필드 자체는 GameState 저장/디버깅용으로 남겨 둔다).
 */

import Phaser from 'phaser';

/** 표시 채널. 게이트웨이 채널명은 GameScene에서 이 값으로 매핑돼 들어온다. */
export type ChatChannel =
  | 'all'       // 일반 (normal)
  | 'shout'     // 외침
  | 'tell'      // 귓속말 (whisper)
  | 'party'     // 파티
  | 'clan'      // 혈맹
  | 'alliance'  // 연합
  | 'trade'     // 거래
  | 'world'     // 전체
  | 'system';   // 클라이언트/서버 시스템 알림

export interface ChatMessage {
  type: ChatChannel;
  sender: string;
  message: string;
  timestamp: number;
}

export interface ChatBoxConfig {
  maxMessages: number;
  width: number;
  height: number;
  x: number;
  y: number;
}

export class ChatBox extends Phaser.GameObjects.Container {
  private messages: ChatMessage[] = [];
  private messageTexts: Phaser.GameObjects.Text[] = [];
  private config: ChatBoxConfig;

  private chatInput!: HTMLInputElement;
  private chatInputContainer!: Phaser.GameObjects.DOMElement;
  private inputVisible: boolean = false;
  private currentChannel: string = 'all';

  private chatHistoryIndex: number = -1;
  private chatHistory: string[] = [];

  /**
   * 채널 색상. 탭 버튼과 메시지 줄이 **같은 표**를 쓰므로 색이 어긋나지 않는다.
   * 원작 라니지의 채널 색 감각에 맞춘 값이다.
   */
  private readonly channelColors: Record<string, string> = {
    all: '#ffffff',       // 흰색   — 일반
    shout: '#ffaa00',     // 주황   — 외침
    world: '#66ccff',     // 하늘   — 전체
    clan: '#44dd44',      // 초록   — 혈맹
    party: '#ffdd44',     // 노랑   — 파티
    trade: '#cc88ff',     // 보라   — 거래
    alliance: '#44dddd',  // 청록   — 연합
    tell: '#ff88cc',      // 분홍   — 귓속말
    system: '#aaaaaa'     // 회색   — 시스템
  };

  /**
   * 메시지 앞에 붙는 채널 말머리.
   * 색만으로는 구분이 어려운(색약·저채도 화면) 채널을 글자로도 구분한다.
   * 일반/시스템은 대부분의 줄을 차지하므로 말머리를 붙이지 않는다.
   */
  private readonly channelTags: Record<string, string> = {
    shout: '[외침]',
    world: '[전체]',
    clan: '[혈맹]',
    party: '[파티]',
    trade: '[거래]',
    alliance: '[연합]',
    tell: '[귓속말]'
  };

  // 하단 탭 목록 (탭이 없는 world/alliance/tell/system은 ALL 탭에서 본다)
  private readonly channels = ['all', 'shout', 'party', 'clan', 'trade'];

  /** 메시지 영역 아래 채널 탭 줄의 실측 높이. createChannelTabs에서 채운다. */
  private tabHeight: number = 0;
  /** 메시지 영역과 채널 탭 줄 사이 간격. createChannelTabs의 tabY와 같은 값이어야 한다. */
  private static readonly TAB_GAP = 5;

  constructor(scene: Phaser.Scene, config?: Partial<ChatBoxConfig>) {
    const defaultConfig: ChatBoxConfig = {
      // GameState.maxChatMessages(100)와 맞춘 값이다.
      // 화면에 한 번에 보이는 줄은 7줄 남짓이지만, 탭 필터로 특정 채널만 볼 때
      // 버퍼가 작으면 "방금 온 혈맹 채팅"이 다른 채널 잡담에 밀려 사라진다.
      maxMessages: 100,
      width: 400,
      height: 150,
      x: 10,
      y: scene.cameras.main.height - 200
    };

    super(scene, config?.x ?? defaultConfig.x, config?.y ?? defaultConfig.y);
    this.config = { ...defaultConfig, ...config };
    scene.add.existing(this);

    // 채팅창은 화면에 고정
    this.setScrollFactor(0);

    this.createUI();
    this.setupInput();
    this.setDepth(50);
  }

  private createUI(): void {
    const { width, height } = this.config;

    // 채팅 배경
    const bg = this.scene.add.graphics();
    bg.fillStyle(0x000000, 0.7);
    bg.fillRoundedRect(0, 0, width, height, 6);
    bg.lineStyle(1, 0x3a3a5a);
    bg.strokeRoundedRect(0, 0, width, height, 6);
    this.add(bg);

    // 채널 탭들
    this.createChannelTabs();

    // 메시지 영역 (메시지 텍스트들이 이 컨테이너에 추가됨)
    this.createMessageArea();

    // 입력 필드 생성
    this.createInputField();
  }

  private createChannelTabs(): void {
    const tabY = this.config.height + ChatBox.TAB_GAP;
    let tabX = 0;

    this.channels.forEach((channel, index) => {
      const color = this.channelColors[channel] || '#ffffff';
      const label = channel.toUpperCase();

      const tab = this.scene.add.text(tabX, tabY, label, {
        fontSize: '10px',
        fontFamily: 'Arial, sans-serif',
        color: index === 0 ? '#ffd700' : color,
        backgroundColor: index === 0 ? '#3a3a5a' : '#1a1a2e',
        padding: { x: 8, y: 4 }
      });

      tab.setOrigin(0, 0);
      // 컨테이너의 setScrollFactor(0)은 자식에 상속되지 않아 직접 걸어야 한다
      // (카메라 스크롤 중 클릭 히트테스트가 어긋나는 문제 방지).
      tab.setInteractive({ useHandCursor: true });
      tab.setScrollFactor(0);
      tab.on('pointerdown', () => this.selectChannel(channel));
      tab.on('pointerover', () => {
        if (channel !== this.currentChannel) {
          tab.setStyle({ backgroundColor: '#2a2a3e' });
        }
      });
      tab.on('pointerout', () => {
        if (channel !== this.currentChannel) {
          tab.setStyle({ backgroundColor: '#1a1a2e' });
        }
      });

      tab.name = `tab_${channel}`;
      this.add(tab);

      // 탭 높이는 폰트·패딩에 따라 Phaser가 계산하므로 실측값을 보관한다
      // (getTotalHeight가 이 값을 쓴다).
      this.tabHeight = Math.max(this.tabHeight, tab.height);

      tabX += tab.width + 5;
    });

    // 숨김 토글 버튼
    const hideBtn = this.scene.add.text(this.config.width - 40, tabY, '[X]', {
      fontSize: '10px',
      fontFamily: 'Arial, sans-serif',
      color: '#888888'
    });
    hideBtn.setOrigin(0, 0);
    // 컨테이너의 setScrollFactor(0)은 자식에 상속되지 않아 직접 걸어야 한다
    // (카메라 스크롤 중 클릭 히트테스트가 어긋나는 문제 방지).
    hideBtn.setInteractive({ useHandCursor: true });
    hideBtn.setScrollFactor(0);
    hideBtn.on('pointerdown', () => this.setVisible(false));
    this.add(hideBtn);
  }

  private createMessageArea(): void {
    const padding = 8;
    const messageHeight = 18;
    const visibleLines = Math.floor((this.config.height - 20) / messageHeight);

    // 메시지 텍스트들을 미리 생성
    for (let i = 0; i < visibleLines; i++) {
      const text = this.scene.add.text(padding, 5 + i * messageHeight, '', {
        fontSize: '13px',
        fontFamily: 'Arial, sans-serif',
        color: '#ffffff',
        wordWrap: { width: this.config.width - padding * 2 }
      });
      text.setVisible(false);
      this.messageTexts.push(text);
      this.add(text);
    }
  }

  private createInputField(): void {
    // HTML 입력 필드 생성
    this.chatInput = document.createElement('input');
    this.chatInput.type = 'text';
    this.chatInput.maxLength = 120;
    this.chatInput.placeholder = 'Press Enter to chat...';
    this.chatInput.style.cssText = `
      width: ${this.config.width - 20}px;
      padding: 6px 10px;
      font-size: 13px;
      font-family: Arial, sans-serif;
      background: #0a0a15;
      border: 1px solid #3a3a5a;
      border-radius: 4px;
      color: #ffffff;
      outline: none;
    `;

    this.chatInput.addEventListener('focus', () => {
      this.chatInput.style.borderColor = '#6a6aaa';
    });

    this.chatInput.addEventListener('blur', () => {
      this.chatInput.style.borderColor = '#3a3a5a';
      this.chatInput.style.outline = 'none';
      this.chatInput.style.boxShadow = 'none';
      this.hideInput();
    });

    this.chatInput.addEventListener('keydown', (e) => {
      // Phaser 키보드 매니저(window 리스너)의 keydown-ENTER/ESC와 이중 처리되지
      // 않도록 전파를 차단한다 (Enter로 전송 직후 다시 입력창이 열리거나,
      // ESC로 입력 닫기가 메뉴 토글로 번지는 문제 방지).
      e.stopPropagation();
      if (e.key === 'Enter') {
        e.preventDefault();
        this.handleSendMessage();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        this.hideInput();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        this.navigateHistory(-1);
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        this.navigateHistory(1);
      }
    });

    // Phaser DOM 요소로 추가
    // 위치는 Container local 좌표가 아닌 scene 절대 좌표를 사용한다.
    // ChatBox 컨테이너가 (config.x, config.y)에 있고 너비 config.width이므로,
    // 입력 필드의 절대 x는 config.x + 10, 절대 y는 config.y + config.height + 5.
    this.chatInputContainer = this.scene.add.dom(
      this.config.x + this.config.width / 2,
      this.config.y + this.config.height + 5,
      this.chatInput
    );
    this.chatInputContainer.setScrollFactor(0);
    this.chatInputContainer.setVisible(false);
    this.scene.add.existing(this.chatInputContainer);
  }

  private setupInput(): void {
    // Enter 키로 채팅 입력 시작
    this.scene.input.keyboard?.on('keydown-ENTER', () => {
      if (!this.inputVisible) {
        // [X] 버튼으로 숨긴 채팅창도 Enter로 다시 표시한다 (복구 수단).
        if (!this.visible) {
          this.setVisible(true);
        }
        this.showInput();
      }
    });

    // Escape로 입력 숨기기
    this.scene.input.keyboard?.on('keydown-ESC', () => {
      if (this.inputVisible) {
        this.hideInput();
      }
    });
  }

  /**
   * 채널 선택.
   *
   * 탭은 두 가지를 동시에 정한다 — **보이는 메시지의 필터**(isVisibleInCurrentTab)와
   * **입력창의 기본 전송 채널**(showInput의 접두사). 예전에는 탭 스타일만 바뀌고
   * 필터가 없어서 어느 탭을 눌러도 같은 목록이 보였다.
   */
  private selectChannel(channel: string): void {
    this.currentChannel = channel;

    // 탭 스타일 업데이트
    this.channels.forEach((ch) => {
      const tab = this.getByName(`tab_${ch}`) as Phaser.GameObjects.Text;
      if (tab) {
        const isActive = ch === channel;
        tab.setStyle({
          color: isActive ? '#ffd700' : this.channelColors[ch],
          backgroundColor: isActive ? '#3a3a5a' : '#1a1a2e'
        });
      }
    });

    // 필터가 바뀌었으므로 목록을 다시 그린다.
    this.renderMessages();
  }

  /**
   * 현재 선택된 탭에서 이 메시지를 보여줄지.
   *
   * - ALL 탭: 전부
   * - 그 외 탭: 해당 채널 + 시스템 메시지
   *   (시스템 알림은 "사망했습니다" 같은 놓치면 안 되는 안내라 어느 탭에서도 남긴다)
   */
  private isVisibleInCurrentTab(msg: ChatMessage): boolean {
    if (this.currentChannel === 'all') return true;
    return msg.type === this.currentChannel || msg.type === 'system';
  }

  /**
   * 메시지 추가
   */
  public addMessage(chatMsg: ChatMessage): void {
    this.messages.push(chatMsg);

    // 최대 메시지 수 제한
    if (this.messages.length > this.config.maxMessages) {
      this.messages.shift();
    }

    this.renderMessages();
  }

  /**
   * 메시지 렌더링.
   *
   * 현재 탭 필터를 통과한 것 중 **최근 것부터** 화면 줄 수만큼 그린다.
   * 발신자(msg.sender)는 출력하지 않는다 — 서버 본문에 이미 들어 있다
   * (파일 상단 "발신자를 다시 붙이지 않는다" 참조).
   */
  private renderMessages(): void {
    // 모든 텍스트 숨기기
    this.messageTexts.forEach((text) => text.setVisible(false));

    const filtered = this.messages.filter((msg) => this.isVisibleInCurrentTab(msg));

    // 최근 메시지부터 표시
    const visibleCount = Math.min(filtered.length, this.messageTexts.length);
    const startIndex = filtered.length - visibleCount;

    for (let i = 0; i < visibleCount; i++) {
      const msg = filtered[startIndex + i];
      const textComponent = this.messageTexts[i];
      if (!textComponent) continue;

      const color = this.channelColors[msg.type] || '#ffffff';
      const tag = this.channelTags[msg.type];
      const time = new Date(msg.timestamp).toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit'
      });

      textComponent.setText(`[${time}] ${tag ? `${tag} ` : ''}${msg.message}`);
      textComponent.setStyle({
        color: color,
        fontSize: '13px',
        fontFamily: 'Arial, sans-serif'
      });
      textComponent.setVisible(true);
    }
  }

  /**
   * 메시지 전송 처리
   */
  private handleSendMessage(): void {
    const raw = this.chatInput.value.trim();

    if (!raw) {
      this.hideInput();
      return;
    }

    // "/party 안녕" 같은 접두사를 파싱해 채널과 본문을 분리한다
    // (분리하지 않으면 접두사가 본문에 섞인 채 서버로 전송된다).
    let channel = this.currentChannel;
    let message = raw;
    if (raw.startsWith('/')) {
      const spaceIdx = raw.indexOf(' ');
      const cmd = (spaceIdx === -1 ? raw.slice(1) : raw.slice(1, spaceIdx)).toLowerCase();
      const body = spaceIdx === -1 ? '' : raw.slice(spaceIdx + 1).trim();

      if (cmd === 'tell' || cmd === 'whisper' || cmd === 'w') {
        // 게이트웨이가 tell을 인식하지 못해 일반챗으로 강등된다 —
        // 귓속말 내용이 공개 채팅으로 새지 않도록 전송하지 않고 안내만 한다.
        this.addSystemMessage('귓속말(/tell)은 아직 지원되지 않는 기능입니다.');
        this.chatInput.value = '';
        this.hideInput();
        return;
      }

      const knownChannels = ['all', 'shout', 'party', 'clan', 'trade', 'world'];
      if (knownChannels.includes(cmd)) {
        channel = cmd;
        message = body;
      }
    }

    if (!message) {
      this.chatInput.value = '';
      this.hideInput();
      return;
    }

    // 채팅 기록에는 입력 원문(접두사 포함)을 저장한다 —
    // navigateHistory가 원문 그대로 복원하므로 접두사가 이중으로 붙지 않는다.
    this.chatHistory.unshift(raw);
    if (this.chatHistory.length > 50) {
      this.chatHistory.pop();
    }
    this.chatHistoryIndex = -1;

    // 메시지 전송 이벤트 발생 (채널과 본문 분리)
    this.emit('send', {
      type: channel,
      message: message
    });

    // 입력 필드 초기화
    this.chatInput.value = '';
    this.hideInput();
  }

  /**
   * 입력 표시
   *
   * DOM이 화면에 렌더링되기 전에 focus()를 호출하면 브라우저가 무시한다.
   * Phaser DOM 요소는 setVisible 후 layout이 한 프레임 뒤에 완료되므로
   * requestAnimationFrame으로 한 프레임 뒤에 focus + select 한다.
   */
  public showInput(): void {
    this.inputVisible = true;
    this.chatInputContainer.setVisible(true);

    // 채널 접두사 설정
    const prefix = this.currentChannel !== 'all' ? `/${this.currentChannel} ` : '';
    this.chatInput.value = prefix;

    // 입력 커서(포커스)를 폼에 위치시키기 위해 한 프레임 뒤에 focus.
    // 두 단계로 분리: 즉시 호출(레이아웃 직전) + rAF 호출(레이아웃 후 보장)
    const placeCaret = (): void => {
      try {
        this.chatInput.focus({ preventScroll: true });
        // 기존 prefix 가 있으면 선택해서, 사용자가 바로 타이핑하면 prefix 가 대체되도록.
        this.chatInput.select();
        // 포커스된 상태에서 텍스트 caret 이 보이도록 시각적 포커스 링 추가
        this.chatInput.style.outline = '1px solid #ffd700';
        this.chatInput.style.boxShadow = '0 0 4px rgba(255, 215, 0, 0.5)';
      } catch {
        // 브라우저 환경에 따라 focus() 가 예외를 던질 수 있음 (예: 비가시 상태) — 무시
      }
    };

    // 즉시 시도 + 다음 프레임에 재시도하여 다양한 타이밍 환경에서 안정적으로 포커스
    placeCaret();
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(() => placeCaret());
    } else {
      this.scene.time.delayedCall(16, placeCaret);
    }
  }

  /**
   * 채팅 입력창이 열려 포커스를 받을 수 있는 상태인지 여부.
   * 이동 등 게임 단축키가 채팅 타이핑과 겹치지 않도록 GameScene에서 참조한다.
   */
  public isInputActive(): boolean {
    return this.inputVisible;
  }

  /**
   * 입력 숨기기
   */
  public hideInput(): void {
    this.inputVisible = false;
    this.chatInputContainer.setVisible(false);
    this.chatInput.blur();
  }

  /**
   * 채팅 기록 탐색
   */
  private navigateHistory(direction: number): void {
    if (this.chatHistory.length === 0) return;

    this.chatHistoryIndex += direction;

    if (this.chatHistoryIndex < 0) {
      this.chatHistoryIndex = 0;
    } else if (this.chatHistoryIndex >= this.chatHistory.length) {
      this.chatHistoryIndex = this.chatHistory.length - 1;
      return;
    }

    // 기록에는 입력 원문(접두사 포함)이 저장되어 있으므로 그대로 복원한다
    // (여기서 접두사를 다시 붙이면 "/party /party ..."처럼 이중 누적된다).
    this.chatInput.value = this.chatHistory[this.chatHistoryIndex];
  }

  /**
   * 시스템 메시지 추가 (편의 메서드)
   */
  public addSystemMessage(message: string): void {
    this.addMessage({
      type: 'system',
      sender: 'System',
      message: message,
      timestamp: Date.now()
    });
  }

  /**
   * 클리어
   */
  public clear(): void {
    this.messages = [];
    this.messageTexts.forEach((text) => {
      text.setText('');
      text.setVisible(false);
    });
  }

  /**
   * 표시/숨기기 토글
   */
  public toggle(): void {
    this.setVisible(!this.visible);
  }

  /**
   * 컨테이너를 옮길 때 DOM 입력 필드도 함께 옮긴다.
   *
   * 입력 필드는 Phaser DOM 요소로 씬 절대 좌표에 배치돼 있어 컨테이너의 자식이 아니다.
   * 이 오버라이드가 없으면 채팅창만 움직이고 입력창은 원래 자리에 남는다.
   */
  public setPosition(x?: number, y?: number, z?: number, w?: number): this {
    super.setPosition(x, y, z, w);

    // Container 생성자가 setPosition을 부르는 시점에는 config가 아직 없다.
    if (this.config) {
      this.config.x = this.x;
      this.config.y = this.y;
      this.chatInputContainer?.setPosition(
        this.x + this.config.width / 2,
        this.y + this.config.height + ChatBox.TAB_GAP
      );
    }

    return this;
  }

  /**
   * 채팅창이 실제로 차지하는 세로 높이.
   *
   * config.height는 메시지 영역만의 높이라서 그 값으로 화면 하단에 정렬하면
   * 채널 탭 줄이 화면 밖으로 밀려난다. 하단 정렬에는 반드시 이 값을 쓸 것.
   */
  public getTotalHeight(): number {
    return this.config.height + ChatBox.TAB_GAP + this.tabHeight;
  }

  public destroy(fromScene?: boolean): void {
    // DOM 요소 정리
    if (this.chatInput?.parentNode) {
      this.chatInput.parentNode.removeChild(this.chatInput);
    }
    this.chatInputContainer?.destroy();

    super.destroy(fromScene);
  }
}
